import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Form, { ButtonItem, SimpleItem, EmptyItem, GroupItem, ColCountByScreen, Label } from 'devextreme-react/form'
import 'devextreme-react/tag-box'
import { useSelector } from 'react-redux'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import Typography from '@mui/material/Typography'
import { PatternRule } from 'devextreme-react/data-grid'
import { IconButton, Stack, useMediaQuery, Box, Alert } from '@mui/material'
import WestIcon from '@mui/icons-material/West'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

const DESIRED_IMAGE_WIDTH = 750
const DESIRED_IMAGE_HEIGHT = 430
const TARGET_ASPECT_RATIO = DESIRED_IMAGE_WIDTH / DESIRED_IMAGE_HEIGHT
const ASPECT_RATIO_TOLERANCE = 0.02

const AddBannerBarForm = (props) => {
    const navigate = useNavigate()
    const { loading } = useSelector((state) => state.banners)
    const [labelLocation, setLabelLocation] = useState('left')
    const [dimensionError, setDimensionError] = useState(null)
    const objectUrlRef = useRef(null)
    const sm = useMediaQuery('(max-width:600px)')

    useEffect(() => {
        setLabelLocation(sm ? 'top' : 'left')
    }, [sm])

    const validateFileAspectRatio = (file) => {
        if (!file) {
            setDimensionError(null)
            return
        }
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current)
            objectUrlRef.current = null
        }
        const url = URL.createObjectURL(file)
        objectUrlRef.current = url
        const img = new Image()
        img.onload = () => {
            const w = img.naturalWidth
            const h = img.naturalHeight
            const actualRatio = h > 0 ? w / h : 0
            const ratioError = Math.abs(actualRatio - TARGET_ASPECT_RATIO) / TARGET_ASPECT_RATIO
            const ok = ratioError <= ASPECT_RATIO_TOLERANCE
            setDimensionError(
                ok
                    ? null
                    : `Image aspect ratio should match ${DESIRED_IMAGE_WIDTH} × ${DESIRED_IMAGE_HEIGHT} (width:height). Selected image is ${w} × ${h} px.`
            )
        }
        img.onerror = () => setDimensionError('Could not read image dimensions.')
        img.src = url
    }

    useEffect(() => {
        return () => {
            if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
        }
    }, [])

    const submitButtonOptions = {
        text: 'Save',
        type: 'default',
        width: 120,
        useSubmitBehavior: true,
    }

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'Normal',
        width: 120,
        onClick: () =>
            navigate('/settings/configuration/banner-bar', {
                state: { navigateFrom: 'cancel-add-banner-bar' },
            }),
    }

    const validationRules = {
        dateStart: [{ type: 'required', message: 'Date Start is required.' }],
        dateEnd: [{ type: 'required', message: 'Date End is required.' }],
        sortOrder: [{ type: 'required', message: 'Sort order is required.' }],
        file: [
            { type: 'required', message: 'Image is required.' },
            {
                type: 'custom',
                validationCallback: (e) => e.value[0]?.size <= 256 * 1024,
                message: 'Image should not be more than 256 KB.',
            },
        ],
    }

    const navigationLinkEditorOptions = {
        valueChangeEvent: 'keyup',
        maskRules: {
            X: /\d/,
        },
    }

    const navigationLinkPattern = /^(?=.{16,})https:\/\/.*$/

    const handleSubmit = (e) => {
        e?.preventDefault?.()
        if (dimensionError) return
        props.addBannerBar(e)
    }

    return (
        <React.Fragment>
            <JumboCardQuick
                title={
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <IconButton
                            className='backArrowForm'
                            onClick={() =>
                                navigate(`/settings/configuration/banner-bar`, {
                                    state: { navigateFrom: 'cancel-add-banner-bar' },
                                })
                            }>
                            <WestIcon />
                        </IconButton>
                        <Typography color={'common.white'} variant={'h3'} sx={{ mt: 0.8 }}>
                            Add Banner Bar
                        </Typography>
                    </Stack>
                }
                headerSx={{
                    height: 40,
                    bgcolor: 'primary.main',
                }}>
                <form action='' onSubmit={handleSubmit}>
                    <Form
                        mode='form'
                        formData={props.BannerBarData}
                        showColonAfterLabel={false}
                        showValidationSummary={false}
                        labelLocation={labelLocation}
                        onFieldDataChanged={(e) => {
                            if (e.dataField === 'file') validateFileAspectRatio(e.value?.[0] ?? null)
                        }}>
                        <GroupItem caption='New Banner Bar' cssClass='form-group-item'>
                            <ColCountByScreen lg={13} md={13} sm={1} xs={1} />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem cssClass='form-group-item' colSpan={6}>
                                <EmptyItem />
                                <SimpleItem dataField='subText'>
                                    <Label text='Sub Text' />
                                </SimpleItem>
                                <SimpleItem dataField='navigationLink' editorOptions={navigationLinkEditorOptions}>
                                    <Label text='Navigation Link' />
                                    <PatternRule
                                        message='Navigation link must start with https:// and be at least 16 characters.'
                                        pattern={navigationLinkPattern}
                                    />
                                </SimpleItem>
                                <SimpleItem
                                    dataField='dateStart'
                                    editorType='dxDateBox'
                                    validationRules={validationRules.dateStart}
                                    editorOptions={{
                                        displayFormat: 'shortDateShortTime',
                                        type: 'datetime',
                                        pickerType: 'calendar',
                                        useMaskBehavior: true,
                                    }}
                                />
                                <SimpleItem
                                    dataField='dateEnd'
                                    editorType='dxDateBox'
                                    validationRules={validationRules.dateEnd}
                                    editorOptions={{
                                        displayFormat: 'shortDateShortTime',
                                        type: 'datetime',
                                        pickerType: 'calendar',
                                        useMaskBehavior: true,
                                    }}
                                />
                                <SimpleItem dataField='orderBy' validationRules={validationRules.sortOrder}>
                                    <Label text='Sort Order' />
                                </SimpleItem>
                                <SimpleItem>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 1 }}>
                                        <Alert
                                            severity='info'
                                            icon={<InfoOutlinedIcon fontSize='small' />}
                                            sx={{
                                                py: 0.5,
                                                maxWidth: 280,
                                                '& .MuiAlert-message': { fontSize: '0.8125rem' },
                                            }}>
                                            Recommended image aspect ratio: {DESIRED_IMAGE_WIDTH} × {DESIRED_IMAGE_HEIGHT} (width × height). Max file size 256 KB.
                                        </Alert>
                                    </Box>
                                </SimpleItem>
                                {dimensionError && (
                                    <SimpleItem>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 1 }}>
                                            <Alert
                                                severity='warning'
                                                sx={{
                                                    py: 0.5,
                                                    maxWidth: 340,
                                                    '& .MuiAlert-message': { fontSize: '0.8125rem' },
                                                }}>
                                                {dimensionError}
                                            </Alert>
                                        </Box>
                                    </SimpleItem>
                                )}
                                <SimpleItem
                                    dataField='file'
                                    editorType='dxFileUploader'
                                    validationRules={validationRules.file}
                                    editorOptions={{
                                        uploadMode: 'useForm',
                                        multiple: false,
                                        selectButtonText: 'Select file',
                                        labelText: '',
                                        accept: 'image/*',
                                        allowedFileExtensions: ['.jpg', '.jpeg', '.png'],
                                        maxFileSize: 1024 * 512,
                                    }}>
                                    <Label text={`Select an Image (${DESIRED_IMAGE_WIDTH} × ${DESIRED_IMAGE_HEIGHT} px)`} />
                                </SimpleItem>
                            </GroupItem>
                        </GroupItem>
                        <EmptyItem />
                        <GroupItem>
                            <ColCountByScreen lg={2} md={2} sm={2} xs={2} />
                            <ButtonItem
                                buttonOptions={{
                                    disabled: loading || !!dimensionError,
                                    icon: loading ? 'refresh' : 'check',
                                    ...submitButtonOptions,
                                }}
                                horizontalAlignment='right'
                            />
                            <ButtonItem buttonOptions={cancelButtonOptions} horizontalAlignment='left' />
                        </GroupItem>
                    </Form>
                </form>
            </JumboCardQuick>
        </React.Fragment>
    )
}

export default AddBannerBarForm
